import { useState, useEffect, useCallback, useRef, useContext } from 'react'
import { useSelector, useDispatch, shallowEqual } from 'react-redux'
import { createUseStyles } from 'react-jss'
import { useParams } from 'react-router-dom'
import CreateJs from 'preload-js'
import { Context } from '@/context'
import LocomotiveScroll from '@/components/LocomotiveScroll'
import Meta from '@/components/Meta'
import Hero from '@/components/Hero'
import BreadcrumbNav from '@/components/BreadcrumbNav'
import CategoryDescription from '@/components/CategoryDescription'
import RevealGrid from '@/components/RevealGrid'
import ServiceItem from '@/components/ServiceItem'
import Footer from '@/components/Footer'
import useHandleAllMediaWithCb from '@/hooks/useHandleAllMediaWithCb'
import actions from '@/actions'
import { decodeEntities } from '@/utils/decodeEntities'
import style from './style'

const useStyles = createUseStyles(style)

const ArchiveServices = ({
  tax: taxFromProps,
  cpt: cptFromProps,
}) => {
  const { getImageUrl, setPageAnimationReady, headerHeight } = useContext(Context)
  const classes = useStyles({ headerHeight })
  const [load, setLoad] = useState(false)
  const [services, setServices] = useState([])
  const preload = useRef(new CreateJs.LoadQueue())
  const $root = useRef()
  const params = useParams()

  /*------------------------------
  Executes a callback on loading each image
  ------------------------------*/
  useHandleAllMediaWithCb({
    init: load,
    ref: $root.current?.ref,
  })

  /*------------------------------
  Redux Connect
  ------------------------------*/
  const { allServices, serviceList, categories, currentCategory, strings, mediaQueryKey } = useSelector((state) => ({
    allServices: Object.values(state.page.pages) || [],
    categories: state.page.taxonomies[taxFromProps] || [],
    currentCategory: state.page.taxonomies[taxFromProps]?.find((tax) => tax.slug === params[taxFromProps]) || {},
    serviceList: state.page.archive[params[taxFromProps]] || [],
    strings: state.language.string,
    mediaQueryKey: state.mediaQuery.key,
  }), shallowEqual)

  /*------------------------------
  Redux Actions
  ------------------------------*/
  const dispatch = useDispatch()
  const getProducts = useCallback((cpt, tax, term) => dispatch(actions.page.getListTerms(cpt, tax, term)), [dispatch])
  const updatePageReady = useCallback((index) => dispatch(actions.loading.setReady(index)), [dispatch])
  const setSiteLoaded = useCallback(() => dispatch(actions.loading.setSiteLoaded()), [dispatch])

  /*------------------------------
  Complete Preload
  ------------------------------*/
  const completePreload = () => {
    updatePageReady(true)
    setSiteLoaded()
    setLoad(true)
  }

  /*------------------------------
  Preload Main Images: preloading only thre three first image
  ------------------------------*/
  const preloadMedia = () => {
    preload.current.on('complete', completePreload)
    preload.current.loadFile({ id: 'img-featured', src: currentCategory.meta.taxonomy_featured_image_url })
    services.slice(0, 3).map((item) => {
      preload.current.loadFile({ id: `img-${item.id}`, src: item.image })
      return false
    })
  }

  /*------------------------------
  Initialize (API Calls)
  ------------------------------*/
  const initialize = () => {
    getProducts(cptFromProps, taxFromProps, params[taxFromProps])
  }

  /*------------------------------
  Set Product by filtering all products by current slug category
  ------------------------------*/
  useEffect(() => {
    if (serviceList.length > 0) {
      const mappedProducts = allServices.filter((p) => serviceList.includes(p.slug)).map((item) => ({
        id: item.id,
        title: item.title.rendered,
        description: item.excerpt.rendered,
        slug: item.slug,
        link: item.link,
        image: getImageUrl(item.featured_media, 'large', item.slug, 'pages'),
      }))
      setServices(mappedProducts)
    }
  }, [serviceList])

  /*------------------------------
  Did Mount
  ------------------------------*/
  useEffect(() => {
    if (!load) initialize()
  }, [])

  /*------------------------------
  Activate Page Animation
  ------------------------------*/
  useEffect(() => {
    if (load) setPageAnimationReady(true)
  }, [load])

  /*------------------------------
  If there are products in category complete the preload
  ------------------------------*/
  useEffect(() => {
    if (services.length > 0) preloadMedia()
  }, [services])

  /*------------------------------
  Render Helmet
  ------------------------------*/
  const renderHelmet = () => {
    const title = decodeEntities(currentCategory.yoast_title || '') || decodeEntities(currentCategory.name || '') || ''
    return load && (
      <Meta
        title={title}
        meta={[
          { name: 'description', content: currentCategory.yoast_desc || '' },
          { property: 'og:title', content: `${title} - ${strings.site_title}` },
          { property: 'og:description', content: currentCategory.yoast_desc || '' },
        ]}
      />
    )
  }

  /*------------------------------
  Render Hero
  ------------------------------*/
  const renderHero = () => {
    return categories.length > 0 && (
      <Hero
        img={currentCategory.meta.taxonomy_featured_image_url}
        heightPercent={mediaQueryKey > 2 ? '30%' : '56.25%'}
      />
    )
  }

  /*------------------------------
  Render Categories Menu
  ------------------------------*/
  const renderCategoriesMenu = () => {
    return categories.length > 1 && (
      <div className={classes.wrapper}>
        <BreadcrumbNav
          items={categories.map((cat) => ({
            id: cat.id,
            title: cat.name,
            link: cat.link,
            slug: cat.slug,
          }))}
        />
      </div>
    )
  }

  /*------------------------------
  Render Category Description
  ------------------------------*/
  const renderDescription = () => {
    return (
      <div className={`${classes.wrapper} ${classes.description}`}>
        <CategoryDescription
          pretitle={currentCategory.name}
          info={currentCategory.description}
          title={currentCategory.meta.taxonomy_featured_image_short_info}
        />
      </div>
    )
  }

  /*------------------------------
  Render Services
  ------------------------------*/
  const renderServices = () => {
    return services.length > 0 && (
      <div className={`${classes.wrapper} ${classes.services}`}>
        <RevealGrid cols={1}>
          {services.map((product) => (
            <ServiceItem
              key={product.id}
              item={product}
            />
          ))}
        </RevealGrid>
      </div>
    )
  }

  /*------------------------------
  Render Footer
  ------------------------------*/
  const renderFooter = () => <Footer />

  /*------------------------------
  Render Content
  ------------------------------*/
  const renderContent = () => {
    return load && (
      <>
        {renderHero()}
        {renderCategoriesMenu()}
        {renderDescription()}
        {renderServices()}
        {renderFooter()}
      </>
    )
  }

  return (
    <LocomotiveScroll
      init={load}
      className={`page pageArchiveServices ${classes.root}`}
      ref={$root}
    >
      {renderHelmet()}
      {renderContent()}
    </LocomotiveScroll>
  )
}

export default ArchiveServices
